import React from 'react'
import { observer } from 'mobx-react-lite'

import Avatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import { ANALYTICS_EVENTS, Drawer, useGlobalCopyStore } from '../../../../../common'

import type { Transaction } from '../../../../../core'

import { useTransactionsUiStore } from '../../../../context/hooks'
import { useEvent, useTransactionStore } from '../../../../../common'
import { ON_CLICK_EVENTS } from '../../../../constants/OnClickEvents'

import TransactionAmountHeader from '../TransactionAmountHeader'

interface MemoActionProps {
  transaction: Transaction
}

const MemoAction: React.FC<MemoActionProps> = ({ transaction }) => {
  const { onEvent } = useEvent()
  const { updateTransaction } = useTransactionStore()
  const { updateManualTransaction } = useTransactionsUiStore()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()

  const theme = useTheme()

  const [open, setOpen] = React.useState(false)
  const [memoText, setMemoText] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setMemoText(transaction.memo ? transaction.memo : '')
    }
  }, [open])

  const handleSave = async () => {
    if (transaction.is_manual && !transaction.guid) {
      updateManualTransaction({ ...transaction, memo: memoText })
      setOpen(false)
      return
    }

    await updateTransaction({ ...transaction, memo: memoText })
    onEvent(ANALYTICS_EVENTS.TRANSACTION_DETAILS_CLICK_MEMO, { transaction_guid: transaction.guid })
    setOpen(false)
  }

  const bgColor = theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'

  return (
    <ListItem className="mx-txn-memo-action" disableGutters={true} disablePadding={true}>
      <ListItemButton
        onClick={() => {
          onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_MEMO_CLICK)
          setOpen(true)
        }}
      >
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: bgColor }} variant="rounded">
            <Icon name="article" sx={{ color: 'text.primary' }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={copy.memo_title}
          secondary={
            <Text bold={true} sx={{ color: 'text.primary' }} variant="body1">
              {transaction.memo ? transaction.memo : copy.memo_desc}
            </Text>
          }
          slotProps={{ primary: { variant: 'caption', fontSize: 11, fontWeight: 'normal' } }}
        />
        <ListItemIcon>
          <Icon name="chevron_right" sx={{ color: 'text.primary' }} />
        </ListItemIcon>
      </ListItemButton>

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={open}
        onClose={() => setOpen(false)}
        onPrimaryAction={handleSave}
        title={copy.memo_title}
      >
        <TransactionAmountHeader transaction={transaction} />
        <TextField
          aria-label={copy.memo_desc_aria}
          focused={true}
          multiline={true}
          name="memo"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemoText(e.target.value)}
          placeholder={copy.memo_desc}
          rows={4}
          sx={{ mx: 16, width: 'calc(100% - 32px)' }}
          value={memoText}
        />
      </Drawer>
    </ListItem>
  )
}

export default observer(MemoAction)
